/**
 * PDF Menu Generator for Middle Street Coffee
 * Generates a downloadable PDF menu with live prices from the API
 */

/**
 * Generate PDF menu using browser's print functionality
 * This is a lightweight solution that doesn't require external libraries
 * For a more sophisticated PDF, you can later integrate jsPDF or pdfmake
 */
export const generateMenuPDF = (menuItems) => {
  try {
    // Use menu items passed from App.js (already fresh from API)
    const coffeeItems = menuItems;

    // Create a printable HTML document
    const menuHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Middle Street Coffee - Menu</title>
        <style>
          @page {
            size: A4;
            margin: 2cm;
          }
          body {
            font-family: 'Georgia', serif;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px;
            color: #1a1a1a;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #2a5932;
            padding-bottom: 20px;
            margin-bottom: 40px;
          }
          h1 {
            font-size: 32px;
            font-weight: 300;
            letter-spacing: 0.05em;
            margin: 0 0 10px 0;
          }
          .tagline {
            font-size: 14px;
            color: #666;
            font-style: italic;
          }
          .info {
            text-align: center;
            font-size: 12px;
            color: #666;
            margin-bottom: 30px;
          }
          .category {
            margin-bottom: 40px;
          }
          .category h2 {
            color: #2a5932;
            font-size: 20px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            border-bottom: 1px solid #e8e3d6;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }
          .menu-items {
            margin: 0;
            padding: 0;
            list-style: none;
          }
          .menu-item {
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            padding: 8px 0;
          }
          .item-name {
            font-size: 16px;
            font-weight: 500;
          }
          .item-dots {
            flex: 1;
            border-bottom: 2px dotted #e8e3d6;
            margin: 0 10px 3px 10px;
          }
          .item-price {
            font-size: 16px;
            font-weight: 500;
            color: #2a5932;
          }
          .note {
            text-align: center;
            font-size: 12px;
            color: #999;
            font-style: italic;
            margin-top: 10px;
          }
          .footer {
            margin-top: 50px;
            text-align: center;
            border-top: 2px solid #e8e3d6;
            padding-top: 20px;
          }
          .footer p {
            font-size: 12px;
            color: #666;
            margin: 5px 0;
          }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Middle Street Coffee</h1>
          <p class="tagline">Specialty Coffee & Teas</p>
        </div>

        <div class="info">
          <p>10 Broad Street, Deal, Kent, CT14 6ER</p>
          <p>Sun-Thu 6:30am - 3:00pm | Fri-Sat 6:30am - 9:00pm</p>
          <p>01304 732 757 | hello@middlestcoffee.shop</p>
        </div>

        <div class="category">
          <h2>Specialty Coffee</h2>
          <ul class="menu-items">
            ${coffeeItems.map(item => `
              <li class="menu-item">
                <span class="item-name">${item.name}</span>
                <span class="item-dots"></span>
                <span class="item-price">${item.price}</span>
              </li>
            `).join('')}
          </ul>
          <p class="note">Try our customer-favorite Oat Milk Latte!</p>
          <p class="note">Milk alternatives: Oat, Almond, Soy, Coconut, Pea (no extra charge)</p>
          <p class="note">Add a flavoured syrup (from 60p)</p>
        </div>

        <div class="category">
          <h2>Tea Selection</h2>
          <ul class="menu-items">
            <li class="menu-item">
              <span class="item-name">English Breakfast</span>
              <span class="item-dots"></span>
              <span class="item-price">£2.80</span>
            </li>
            <li class="menu-item">
              <span class="item-name">Earl Grey</span>
              <span class="item-dots"></span>
              <span class="item-price">£3.00</span>
            </li>
            <li class="menu-item">
              <span class="item-name">Green Tea</span>
              <span class="item-dots"></span>
              <span class="item-price">£3.00</span>
            </li>
            <li class="menu-item">
              <span class="item-name">Herbal Tea</span>
              <span class="item-dots"></span>
              <span class="item-price">£3.00</span>
            </li>
          </ul>
        </div>

        <div class="category">
          <h2>Extras</h2>
          <ul class="menu-items">
            <li class="menu-item">
              <span class="item-name">Baked Goods</span>
              <span class="item-dots"></span>
              <span class="item-price">From £2.50</span>
            </li>
          </ul>
          <p class="note">Ask about our seasonal specials and daily bakes</p>
        </div>

        <div class="footer">
          <p><strong>All drinks available hot or iced</strong></p>
          <p>Dog-Friendly | Expert Baristas | Since 2023</p>
          <p>Order online: ordercoffee.uk</p>
        </div>
      </body>
      </html>
    `;

    // Create a blob and download
    const blob = new Blob([menuHTML], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `middlest-coffee-menu-${new Date().toISOString().split('T')[0]}.html`;
    link.click();
    URL.revokeObjectURL(url);

    // Also open in new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.open();
    printWindow.document.write(menuHTML);
    printWindow.document.close();

    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error generating menu PDF:', error);
    }
    return false;
  }
};

/**
 * Alternative: Generate using jsPDF (requires installing jspdf)
 * Uncomment and use this if you want a true PDF file
 */
/*
import jsPDF from 'jspdf';

export const generateMenuPDFWithLibrary = async (menuItems) => {
  const doc = new jsPDF();

  // Header
  doc.setFontSize(24);
  doc.text('Middle Street Coffee', 105, 20, { align: 'center' });

  doc.setFontSize(12);
  doc.text('Specialty Coffee & Teas', 105, 28, { align: 'center' });

  // Add menu sections...
  // (Full implementation would go here)

  doc.save(`middlest-coffee-menu-${new Date().toISOString().split('T')[0]}.pdf`);
};
*/
